import React, { FC } from "react";

import { eachDayOfInterval } from "date-fns";

import { toDateString } from "~/libs";

import { ProgressTableBase } from "./ProgressTableBase";

type EmployeeProgress = {
  employee: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  progress: Array<{
    postDate: string;
    count: number;
  }>;
};

type ProgressTableForSurveyProgressProps = {
  startDate: Date | null;
  endDate: Date | null;
  userProgress: EmployeeProgress[];
  tableHeader?: React.ReactNode;
  className?: string;
};

/**
 * チェックアウト進捗表示用のProgressTableコンポーネント
 *
 * 日付範囲とユーザー進捗データから、自動的にテーブルデータを生成して表示します。
 * - columns: startDate〜endDateの日付配列（週末判定付き）を自動生成
 * - employees: userProgressから従業員情報を自動抽出
 * - renderCell: 進捗データに基づいてセルを自動レンダリング
 */
export const ProgressTableForSurveyProgress: FC<
  ProgressTableForSurveyProgressProps
> = ({ startDate, endDate, userProgress, tableHeader, className }) => {
  // 日付配列を生成
  const dates =
    startDate && endDate
      ? eachDayOfInterval({ start: startDate, end: endDate }).map((d) =>
          toDateString(d),
        )
      : [];

  // columns生成（週末判定付き）
  const columns = dates.map((date) => ({
    date,
    isWeekend: [0, 6].includes(new Date(date).getDay()),
  }));

  // employees生成
  const employees = userProgress.map((up) => ({
    id: up.employee.id,
    name: up.employee.name,
    avatarUrl: up.employee.avatarUrl,
  }));

  // セルのレンダリング関数
  const renderCell = ({
    date,
    employee,
  }: {
    date: string;
    employee: { id: string; name: string; avatarUrl: string | null };
    isWeekend: boolean;
  }) => {
    const userProgressData = userProgress.find(
      (up) => up.employee.id === employee.id,
    );
    const progress = userProgressData?.progress.find(
      (p) => p.postDate === date,
    );
    const hasPost = (progress?.count || 0) > 0;

    return hasPost ? (
      <span className="inline-block w-4 h-4 bg-primary-600 rounded-xs"></span>
    ) : (
      <span className="inline-block w-4 h-4 border border-gray-300 rounded-xs"></span>
    );
  };

  return (
    <ProgressTableBase
      columns={columns}
      employees={employees}
      renderCell={renderCell}
      tableHeader={tableHeader}
      className={className}
    />
  );
};
